import LegalLayout, { Section } from "@/components/LegalLayout";
import { Mail, MessageSquare, FileText, ShieldCheck, AlertTriangle, Clock } from "lucide-react";

const LAST_UPDATED = "27 February 2026";
const PREFIX = "support";

const Card = ({ icon: Icon, title, children, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-sm transition" data-testid={testid}>
    <div className="flex items-start gap-3">
      <div className="bg-orange-50 p-2 rounded-lg flex-shrink-0"><Icon className="w-5 h-5 text-orange-600" /></div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-slate-900 mb-1">{title}</h3>
        <div className="text-sm text-slate-600 leading-relaxed">{children}</div>
      </div>
    </div>
  </div>
);

/**
 * Support / contact hub — required by Apple Guideline 5.1.1 (clear support
 * contact) and Play's app content reviews. Centralises every contact path so
 * reviewers can verify them quickly.
 */
export default function SupportPage() {
  return (
    <LegalLayout
      title="Support &amp; Contact"
      canonical="https://transportanything247.com/support"
      description="How to get help from Transport Anything 247 — booking issues, driver support, dispute resolution and data requests."
      lastUpdated={LAST_UPDATED}
      testidPrefix={PREFIX}
    >
      <Section id="email" title="Email us" testidPrefix={PREFIX}>
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <p className="text-base font-semibold text-slate-900 mb-1">
            <a href="mailto:customers@transportanything247.com" className="text-orange-600 underline" data-testid="support-email-link">
              customers@transportanything247.com
            </a>
          </p>
          <p className="text-xs text-slate-600">
            Single inbox for customers and drivers. UK office hours Mon–Fri 09:00–17:30; we typically reply the same working day.
          </p>
        </div>
      </Section>

      <Section id="channels" title="Choose the right channel" testidPrefix={PREFIX}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card icon={MessageSquare} title="During a live booking" testid="support-card-live">
            Use the in-app chat panel — it routes to the assigned driver and (for support questions) our operations team in parallel.
          </Card>
          <Card icon={Mail} title="Refunds and disputes" testid="support-card-refunds">
            Email us within 7 days of the booking date. Include the booking ID and a description of the issue.
          </Card>
          <Card icon={FileText} title="Driver verification" testid="support-card-driver">
            Email us with the subject line "Driver verification — &lt;your name&gt;" if you have document upload or approval questions.
          </Card>
          <Card icon={ShieldCheck} title="Data / privacy requests" testid="support-card-privacy">
            Use the dedicated <a href="/data-deletion" className="text-orange-600 underline">Account &amp; Data Deletion</a> page, or email with the subject "Data subject request".
          </Card>
        </div>
      </Section>

      <Section id="urgent" title="Urgent / safety issues" testidPrefix={PREFIX}>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-slate-700">
            If you face an immediate safety risk on the road or during a delivery, contact the emergency
            services first (999 in the UK). For platform-side safety incidents (harassment, fraud, suspicious
            behaviour), email us straight away with the subject line <strong>"URGENT SAFETY"</strong>; that
            inbox is monitored outside office hours on a best-effort basis.
          </div>
        </div>
      </Section>

      <Section id="response-times" title="Expected response times" testidPrefix={PREFIX}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <Clock className="w-4 h-4 text-slate-500 mb-1" />
            <p className="text-xs uppercase tracking-wide text-slate-500">In-app chat (live booking)</p>
            <p className="text-sm font-semibold text-slate-800">&lt; 5 minutes during the job</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <Clock className="w-4 h-4 text-slate-500 mb-1" />
            <p className="text-xs uppercase tracking-wide text-slate-500">Email — general</p>
            <p className="text-sm font-semibold text-slate-800">Same working day</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <Clock className="w-4 h-4 text-slate-500 mb-1" />
            <p className="text-xs uppercase tracking-wide text-slate-500">Data subject requests</p>
            <p className="text-sm font-semibold text-slate-800">Within 1 month (UK GDPR)</p>
          </div>
        </div>
      </Section>

      <Section id="self-service" title="Self-service" testidPrefix={PREFIX}>
        <ul className="list-disc pl-5 space-y-1">
          <li><a href="/privacy" className="text-orange-600 underline">Privacy Policy</a> — what data we collect, how we use it, and your rights.</li>
          <li><a href="/terms" className="text-orange-600 underline">Terms of Service</a> — your contract with us.</li>
          <li><a href="/cookies" className="text-orange-600 underline">Cookie Policy</a> — what storage we use on your device.</li>
          <li><a href="/aup" className="text-orange-600 underline">Acceptable Use Policy</a> — what's allowed on the platform.</li>
          <li><a href="/data-deletion" className="text-orange-600 underline">Account &amp; Data Deletion</a> — how to close your account.</li>
        </ul>
      </Section>
    </LegalLayout>
  );
}
